import { useQuery } from '@tanstack/react-query';
import { Button, Modal } from '@safetyhub/ui';
import { adminApi, extractApiError, type AnnexUnmappedSymbolsView } from '../../lib/api';
import { OpsManualLink } from '../OpsManualLink';
import { formatDateTime } from '../../utils/format';

/** 조회 키 — `['admin','regulations']` 접두의 하위. 값은 수집 뒤 별표 가공(03:00 cron · 「업데이트」)에만 바뀐다 */
export const ANNEX_UNMAPPED_SYMBOLS_QUERY_KEY = ['admin', 'regulations', 'annexes', 'unmapped-symbols'] as const;

/** 복원 상태 → 화면 말(서버 enum `AnnexRestoreStatus`) — 모르는 값은 원문 그대로 보인다 */
const RESTORE_STATUS_LABEL: Record<string, string> = {
  RESTORED: '복원함',
  SOURCE_FAILED: '원 파일 받기·읽기 실패',
  NO_SOURCE: '원 파일 링크 없음',
  DISABLED: '내려받기 꺼짐',
  NOT_NEEDED: '복원 불필요',
};

function formatAt(iso: string | null): string {
  return iso ? formatDateTime(iso) : '없음';
}

/**
 * **별표 복원의 「대응 안 된 기호」** — 법제처 별표 글에서 `?`로 온 자리를 원 파일로 되돌릴 때, 기호 글꼴의 글자 중 대응표에
 * 없어 `?`로 남은 것. 실패가 아니라 «상태»라 운영 사건이 되지 않으므로(서버 `AnnexGridService` 실패 종류 docblock) 이 화면이
 * 그것을 보는 유일한 자리다. 읽기 전용 — 대응표는 코드에 있어 여기서 늘리지 않는다.
 *
 * 집계 규칙(「현재」 run · 별표 키당 1건 · 지금 복원기 버전)은 서버 `summarizeAnnexUnmappedSymbols`가 정한다.
 * ⚠️ **0건은 두 뜻이다** — 복원을 시도한 별표가 있는데 0이면 「다 대응됐다」, 시도한 별표가 0이면 「셀 대상이 없다」다
 * (내려받기 스위치가 꺼져 있거나 `?`가 있는 별표가 아직 가공되지 않았다). 그래서 실행 현황을 목록보다 먼저 그린다.
 */
export default function AnnexUnmappedSymbolsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ANNEX_UNMAPPED_SYMBOLS_QUERY_KEY,
    queryFn: async (): Promise<AnnexUnmappedSymbolsView> => {
      const res = await adminApi.getAnnexUnmappedSymbols();
      if (!res.data) throw new Error('서버 응답에 본문이 없습니다. 잠시 후 다시 시도해 주세요.');
      return res.data;
    },
    enabled: open,
  });

  if (!open) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title="별표 — 대응 안 된 기호"
      size="xl"
      footer={
        <Button variant="secondary" onClick={onClose}>
          닫기
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm text-gray-600">
            별표 원 파일로 <code>?</code>를 되돌릴 때 대응표에 없어 <code>?</code>로 남은 기호입니다. 실패가 아니라 운영 사건·알림이
            생기지 않습니다.
          </p>
          <OpsManualLink topic="annexUnmappedSymbols" className="shrink-0 text-sm text-brand-600 hover:underline" />
        </div>

        {isLoading && <p className="text-sm text-gray-500">불러오는 중...</p>}
        {error && (
          <p role="alert" className="text-sm text-red-600">
            목록을 불러오지 못했습니다. {extractApiError(error).message}
          </p>
        )}
        {data && <UnmappedSymbolsBody data={data} />}
      </div>
    </Modal>
  );
}

function UnmappedSymbolsBody({ data }: { data: AnnexUnmappedSymbolsView }) {
  const { coverage } = data;
  const attempted = coverage.byRestoreKey.CURRENT;
  return (
    <>
      <section aria-label="복원 실행 현황" className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 space-y-1">
        <div>
          원 파일 내려받기: <strong>{data.sourceDownloadEnabled ? '켜짐' : '꺼짐'}</strong>
        </div>
        <div>
          지금 복원기(버전 {data.restorerVersion})로 복원을 시도한 별표: <strong>{attempted}건</strong>
          {attempted > 0 && (
            <span className="text-gray-500">
              {' '}
              (
              {Object.entries(coverage.byRestoreStatus)
                .map(([status, n]) => `${RESTORE_STATUS_LABEL[status] ?? status} ${n}`)
                .join(' · ')}
              )
            </span>
          )}
        </div>
        <div className="text-gray-500">
          <code>?</code>가 있으나 내려받기가 꺼져 복원하지 않은 별표 {coverage.byRestoreKey.OFF}건 · 옛 복원기로만 복원한 별표{' '}
          {coverage.byRestoreKey.OLDER}건 · <code>?</code>가 없는 별표 {coverage.byRestoreKey.NA}건 (가공된 별표 {coverage.annexes}건)
        </div>
        <div className="text-gray-500">
          마지막 복원: {formatAt(coverage.lastRestoreAt)} · 마지막 가공: {formatAt(coverage.lastProcessedAt)}
        </div>
      </section>

      {data.symbols.length === 0 ? (
        attempted === 0 ? (
          <p role="status" className="text-sm text-amber-700">
            복원을 시도한 별표가 없어 셀 대상이 없습니다 — 「다 대응됐다」는 뜻이 아닙니다.
            {!data.sourceDownloadEnabled && ' 원 파일 내려받기가 꺼져 있습니다.'}
          </p>
        ) : (
          <p role="status" className="text-sm text-green-700">
            복원한 별표 {attempted}건에서 대응 안 된 기호가 없습니다.
          </p>
        )
      ) : (
        <table className="w-full text-sm divide-y divide-gray-200">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-2 pr-3 font-medium">글꼴</th>
              <th className="py-2 pr-3 font-medium">코드</th>
              <th className="py-2 pr-3 font-medium text-right">개수</th>
              <th className="py-2 font-medium">나온 별표</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.symbols.map((s) => (
              <tr key={`${s.font ?? ''}|${s.code}`} className="align-top">
                <td className="py-2 pr-3">{s.font ?? '(글꼴 모름)'}</td>
                <td className="py-2 pr-3 font-mono">{s.code}</td>
                <td className="py-2 pr-3 text-right">{s.count}</td>
                <td className="py-2">
                  <ul className="space-y-0.5">
                    {s.annexes.map((a) => (
                      <li key={a.annexKey}>
                        {a.ownerName} · {a.title}
                        <span className="text-gray-400"> ({a.count}개 · {a.annexKey})</span>
                      </li>
                    ))}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
